<script>
import {
    assistantState,
    isAgentAvailable,
    sendAssistantMessage,
    setAgentModel,
    setAssistantMode,
    stopAssistantMessage,
} from "@app/store/assistantStore.svelte.js";
import { removeSavedPrompt, savePrompt, savedPromptState } from "@app/store/savedPromptStore.svelte.js";
import { hasAgentPlan } from "@app/lib/entitlements.js";
import { toast } from "@zerodevx/svelte-toast";
import SavedPromptRemovedToast from "./SavedPromptRemovedToast.svelte";
import * as m from "@app/paraglide/messages.js";

let draft = $state("");
let savedOpen = $state(false);

let isStreaming = $derived(Boolean(assistantState.streamingChatId));
// The Agent toggle shows only on plans that include it (Pro). The server status confirms access.
let agentPlan = $derived(hasAgentPlan());
let agentAvailable = $derived(agentPlan && isAgentAvailable());
let agentMode = $derived(assistantState.mode === "agent" && agentAvailable);
let remaining = $derived(assistantState.status?.remaining);
let agent = $derived(assistantState.status?.agent);
let creditsRemaining = $derived(agent?.creditsRemaining);
let sharedCredits = $derived(Boolean(agent?.shared));
let inOverage = $derived(agentMode && creditsRemaining <= 0 && Boolean(agent?.overageEnabled));
let overageLeft = $derived(inOverage && agent.overageCostUsd < agent.capUsd);
let limitReached = $derived(agentMode ? creditsRemaining <= 0 && !overageLeft : remaining === 0);

function resetDate(ms) {
    return Number.isFinite(ms) ? new Date(ms).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
}

function creditsLine() {
    if (agent.trial) {
        return m.assistant_agent_trial_credits_left({ count: Math.floor(creditsRemaining), limit: agent.creditsLimit });
    }
    if (inOverage) {
        return m.assistant_agent_overage_line({
            credits: Math.floor(agent.overageUsed),
            cost: agent.overageCostUsd.toFixed(2),
            cap: agent.capUsd,
        });
    }
    const left = sharedCredits
        ? m.assistant_agent_team_credits_left({ count: Math.floor(creditsRemaining) })
        : m.assistant_agent_credits_left({ count: Math.floor(creditsRemaining) });
    const date = resetDate(agent.periodEnd);
    return date ? `${left} · ${m.assistant_agent_resets({ date })}` : left;
}
let canSend = $derived(Boolean(draft.trim()) && !isStreaming && !assistantState.restoring && !limitReached);
let input = $state();

// Text handed to the composer: "Edit & restore" passes the rewound prompt
// (a string, always Agent mode); example prompts pass { text, mode }.
$effect(() => {
    const handed = assistantState.composerDraft;
    if (handed === null) return;
    assistantState.composerDraft = null;
    const { text, mode } = typeof handed === "string" ? { text: handed, mode: "agent" } : handed;
    draft = text;
    setAssistantMode(mode);
    input?.focus();
});

function send() {
    if (!canSend) return;
    const question = draft;
    draft = "";
    sendAssistantMessage(question);
}

function handleKeydown(e) {
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing) {
        e.preventDefault();
        send();
    }
}

function saveDraft() {
    savedOpen = false;
    savePrompt({ text: draft, mode: assistantState.mode });
}

function useSavedPrompt(prompt) {
    savedOpen = false;
    assistantState.composerDraft = { text: prompt.text, mode: prompt.mode };
}

async function removePrompt(prompt) {
    const removed = await removeSavedPrompt(prompt.id);
    if (removed) {
        toast.push({
            component: { src: SavedPromptRemovedToast, props: { prompt: removed }, sendIdTo: "toastId" },
            classes: ["toast-info"],
        });
    }
}

/* composedPath, not closest: removing a prompt detaches the clicked row before this runs. */
function closeSavedOnOutsideClick(e) {
    if (savedOpen && !e.composedPath().some((node) => node.classList?.contains("assistant-saved"))) {
        savedOpen = false;
    }
}

function handleSavedKeydown(e) {
    if (e.key === "Escape" && savedOpen) {
        e.preventDefault();
        savedOpen = false;
    }
}
</script>

<svelte:window onclick={closeSavedOnOutsideClick} />

<div class="assistant-composer border-top p-2">
    {#if limitReached && assistantState.status}
        <div class="alert alert-warning small py-1 px-2 mb-2" role="status">
            {#if !agentMode}
                {m.assistant_limit_reached({ limit: assistantState.status.limit })}
            {:else if agent?.trial}
                {m.assistant_error_trial_credits()}
            {:else if inOverage}
                {m.assistant_error_overage_cap()}
            {:else}
                {m.assistant_error_credits()}
            {/if}
        </div>
    {/if}
    <div class={["assistant-input-box", limitReached && "disabled"]}>
        <textarea
            class="assistant-input"
            rows="3"
            placeholder={agentMode ? m.assistant_agent_composer_placeholder() : m.assistant_composer_placeholder()}
            aria-label={m.assistant_composer_label()}
            bind:value={draft}
            bind:this={input}
            onkeydown={handleKeydown}
            disabled={limitReached}
        ></textarea>
        <div class="d-flex align-items-center gap-2 px-2 pb-2">
            <div class="assistant-mode" role="group" aria-label={m.assistant_mode_label()}>
                <button
                    type="button"
                    class={["assistant-mode-btn", !agentMode && "active"]}
                    aria-pressed={!agentMode}
                    disabled={isStreaming}
                    onclick={() => setAssistantMode("ask")}
                >
                    <i class="bi bi-chat-dots" aria-hidden="true"></i>{m.assistant_mode_ask()}
                </button>
                {#if agentPlan}
                    <button
                        type="button"
                        class={["assistant-mode-btn", agentMode && "active"]}
                        aria-pressed={agentMode}
                        disabled={isStreaming || !agentAvailable}
                        onclick={() => setAssistantMode("agent")}
                    >
                        <i class="bi bi-magic" aria-hidden="true"></i>{m.assistant_mode_agent()}
                    </button>
                {/if}
            </div>
            {#if agentMode}
                <div class="assistant-mode" role="group" aria-label={m.assistant_model_label()}>
                    <button
                        type="button"
                        class={["assistant-mode-btn", assistantState.agentModel !== "deep" && "active"]}
                        aria-pressed={assistantState.agentModel !== "deep"}
                        disabled={isStreaming}
                        onclick={() => setAgentModel("standard")}
                    >
                        {m.assistant_model_standard()}
                    </button>
                    <button
                        type="button"
                        class={["assistant-mode-btn", assistantState.agentModel === "deep" && "active"]}
                        aria-pressed={assistantState.agentModel === "deep"}
                        title={m.assistant_model_deep_hint()}
                        disabled={isStreaming}
                        onclick={() => setAgentModel("deep")}
                    >
                        <i class="bi bi-stars" aria-hidden="true"></i>{m.assistant_model_deep()}
                    </button>
                </div>
            {/if}
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div class="position-relative assistant-saved ms-auto" onkeydown={handleSavedKeydown}>
                <button
                    type="button"
                    class={["assistant-saved-btn", savedOpen && "active"]}
                    title={m.assistant_saved_prompts()}
                    aria-label={m.assistant_saved_prompts()}
                    aria-haspopup="menu"
                    aria-expanded={savedOpen}
                    disabled={isStreaming}
                    onclick={() => (savedOpen = !savedOpen)}
                >
                    <i class={["bi", savedPromptState.length ? "bi-bookmark-fill" : "bi-bookmark"]} aria-hidden="true"></i>
                </button>
                {#if savedOpen}
                    <div class="dropdown-menu dropdown-menu-end show assistant-saved-menu small" role="menu">
                        <button type="button" class="dropdown-item" role="menuitem" disabled={!draft.trim()} onclick={saveDraft}>
                            <i class="bi bi-bookmark-plus me-2" aria-hidden="true"></i>{m.assistant_save_prompt()}
                        </button>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header">{m.assistant_saved_prompts()}</h6>
                        {#each savedPromptState as prompt (prompt.id)}
                            <div class="assistant-saved-row d-flex align-items-start">
                                <button
                                    type="button"
                                    class="dropdown-item assistant-saved-text"
                                    role="menuitem"
                                    title={prompt.text}
                                    onclick={() => useSavedPrompt(prompt)}
                                >
                                    <i class={["bi me-2", prompt.mode === "agent" ? "bi-magic" : "bi-chat-dots"]} aria-hidden="true"></i>
                                    <span>{prompt.text}</span>
                                </button>
                                <button
                                    type="button"
                                    class="assistant-saved-remove"
                                    title={m.assistant_saved_prompt_remove()}
                                    aria-label={m.assistant_saved_prompt_remove()}
                                    onclick={() => removePrompt(prompt)}
                                >
                                    <i class="bi bi-x-lg" aria-hidden="true"></i>
                                </button>
                            </div>
                        {:else}
                            <div class="px-3 pb-2 text-muted">{m.assistant_saved_prompts_empty()}</div>
                        {/each}
                    </div>
                {/if}
            </div>
            {#if isStreaming}
                <button type="button" class="btn btn-sm btn-outline-danger assistant-stop" onclick={stopAssistantMessage}>
                    <i class="bi bi-stop-fill me-1" aria-hidden="true"></i>{m.assistant_stop()}
                </button>
            {:else}
                <button
                    type="button"
                    class="btn btn-primary assistant-send"
                    title={m.assistant_send()}
                    aria-label={m.assistant_send()}
                    disabled={!canSend}
                    onclick={send}
                >
                    <i class="bi bi-arrow-up" aria-hidden="true"></i>
                </button>
            {/if}
        </div>
    </div>
    <div class="assistant-composer-meta d-flex px-1 mt-1">
        <span class="text-truncate">
            {#if agentMode}
                {#if Number.isFinite(creditsRemaining)}
                    {creditsLine()}
                {/if}
            {:else if Number.isFinite(remaining)}
                {m.assistant_messages_left({ count: remaining })}
            {/if}
        </span>
        <span class="ms-auto text-nowrap ps-2">{m.assistant_composer_hint()}</span>
    </div>
</div>

<style>
    .assistant-input-box {
        border: 1px solid var(--bs-border-color);
        border-radius: 0.75rem;
        background-color: var(--bs-body-bg);
        transition: border-color 0.15s, box-shadow 0.15s;
    }

    .assistant-input-box:focus-within {
        border-color: var(--bs-primary);
        box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb), 0.2);
    }

    .assistant-input-box.disabled {
        opacity: 0.6;
    }

    .assistant-input {
        display: block;
        width: 100%;
        padding: 0.6rem 0.75rem 0.25rem;
        border: 0;
        background: transparent;
        color: var(--bs-body-color);
        font-size: 0.9rem;
        resize: none;
        outline: none;
    }

    .assistant-input::placeholder {
        color: var(--bs-secondary-color);
    }

    .assistant-mode {
        display: inline-flex;
        padding: 0.15rem;
        border-radius: 999px;
        background-color: var(--bs-tertiary-bg);
        border: 1px solid var(--bs-border-color-translucent);
    }

    .assistant-mode-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.15rem 0.65rem;
        border: 0;
        border-radius: 999px;
        background: transparent;
        color: var(--bs-secondary-color);
        font-size: 0.8rem;
    }

    .assistant-mode-btn:hover:not(:disabled):not(.active) {
        color: var(--bs-body-color);
    }

    .assistant-mode-btn.active {
        background-color: var(--bs-primary);
        color: #fff;
    }

    .assistant-mode-btn:disabled {
        opacity: 0.6;
    }

    .assistant-send {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        padding: 0;
        border-radius: 50%;
    }

    .assistant-stop {
        border-radius: 999px;
    }

    .assistant-saved-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2rem;
        height: 2rem;
        padding: 0;
        border: 0;
        border-radius: 50%;
        background: transparent;
        color: var(--bs-secondary-color);
    }

    .assistant-saved-btn:hover:not(:disabled),
    .assistant-saved-btn.active {
        background-color: var(--bs-secondary-bg);
        color: var(--bs-body-color);
    }

    .assistant-saved-btn:disabled {
        opacity: 0.6;
    }

    .assistant-saved-menu {
        position: absolute;
        right: 0;
        bottom: calc(100% + 0.25rem);
        top: auto;
        z-index: 1050;
        width: 22rem;
        max-width: calc(100vw - 2rem);
        max-height: 50vh;
        overflow-y: auto;
    }

    .assistant-saved-text {
        display: flex;
        align-items: flex-start;
        min-width: 0;
        white-space: normal;
    }

    .assistant-saved-text span {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        overflow-wrap: anywhere;
    }

    .assistant-saved-text i {
        margin-top: 0.15rem;
    }

    .assistant-saved-remove {
        flex-shrink: 0;
        margin: 0.2rem 0.5rem 0 0;
        padding: 0.1rem 0.35rem;
        border: 0;
        border-radius: var(--bs-border-radius);
        background: transparent;
        color: var(--bs-secondary-color);
        font-size: 0.75rem;
    }

    .assistant-saved-remove:hover,
    .assistant-saved-remove:focus-visible {
        background-color: var(--bs-secondary-bg);
        color: var(--bs-danger);
        outline: none;
    }

    .assistant-composer-meta {
        color: var(--bs-secondary-color);
        font-size: 0.75rem;
    }
</style>
